import { DateObjectUnits, DateTime, DateTimeUnit, ToISOTimeOptions } from 'luxon';
import { check, memoize, type } from '@mews-framework/utils';
import { comparable, manipulable } from '../componentEnhancers';
import { SUPPORTED_DATE_TIME_UTC_STRING, UTCTimezone } from '../constants';
import { DateTimePart, ISOFormat } from '../enums';
import { toLuxon } from '../utils';
import DateTimeUtc from './dateTimeUtc';

const DATE_TIME_UTC_FORMATS = [ISOFormat.Year, ISOFormat.UtcSeconds, ISOFormat.UtcMilliseconds];

const PARTS = [
    DateTimePart.Year,
    DateTimePart.Month,
    DateTimePart.Week,
    DateTimePart.Day,
    DateTimePart.Hours,
    DateTimePart.Minutes,
    DateTimePart.Seconds,
] as const;

export const toDateTimeUtc = memoize((dateTimeUtc: DateTimeUtc) => {
    type.check(dateTimeUtc, DateTimeUtc);
    return DateTime.fromMillis(dateTimeUtc.millis, { zone: UTCTimezone });
});

export const DateTimeUtcService = {
    ...comparable(toDateTimeUtc),
    ...manipulable(DateTimeUtc, toDateTimeUtc, PARTS),
    parse(dateTimeUtcString: string) {
        check.string(dateTimeUtcString);

        const luxonDate = toLuxon(dateTimeUtcString, DATE_TIME_UTC_FORMATS);
        return DateTimeUtc(luxonDate);
    },

    fromEpoch(timestamp: number) {
        const luxonDate = DateTime.fromMillis(timestamp);
        return DateTimeUtc(luxonDate);
    },

    getEpoch(dateTimeUtc: DateTimeUtc) {
        const luxonDate = toDateTimeUtc(dateTimeUtc);
        return luxonDate.valueOf();
    },

    /** @deprecated Use `appClockService.getAppNowUtc()` */
    now() {
        const luxonDate = DateTime.utc();
        return DateTimeUtc(luxonDate);
    },

    toISOString(dateTimeUtc: DateTimeUtc, options?: Pick<ToISOTimeOptions, 'suppressMilliseconds'>) {
        const luxonDate = toDateTimeUtc(dateTimeUtc);
        return luxonDate.toUTC().toISO(options);
    },

    startOf(dateTime: DateTimeUtc, part: DateTimeUnit) {
        const luxonDateTime = toDateTimeUtc(dateTime).startOf(part);
        return DateTimeUtc(luxonDateTime);
    },

    endOf(dateTime: DateTimeUtc, part: DateTimeUnit) {
        const luxonDateTime = toDateTimeUtc(dateTime).endOf(part);
        return DateTimeUtc(luxonDateTime);
    },

    set(dateTime: DateTimeUtc, parts: DateObjectUnits) {
        const luxonDateTime = toDateTimeUtc(dateTime).set(parts);
        return DateTimeUtc(luxonDateTime);
    },

    isValidString(dateTimeUtcString: string) {
        try {
            this.parse(dateTimeUtcString);
            return true;
        } catch (error) {
            return false;
        }
    },

    isSupported() {
        return this.isValidString(SUPPORTED_DATE_TIME_UTC_STRING);
    },
};
